import {
  hedgeInstrumentScope,
  resolveDefaultHedgeInstrument,
} from '@/features/hedge/lib/hedge-instrument'
import { HedgeFormModel, HedgePriorityBandForm, PRIORITY_RUNG_KEYS } from '@/features/hedge/types'
import {
  HedgeAlgoParams,
  HedgeDeltaBandsResolved,
  HedgeMechanism,
  HedgePriorityBand,
  HedgeScopeResolved,
} from '@/types/generated/algo'
import { Position } from '@/types/generated/common/trading'
import { Exchange, Instrument, TradeVenue } from '@/types/generated/common/types'

function priorityBand(b: HedgePriorityBandForm): HedgePriorityBand {
  const band: HedgePriorityBand = {
    from: b.from,
    to: b.to,
    target: b.target,
    mechanism: b.mechanism,
  }
  if (b.mechanism === HedgeMechanism.HEDGE_MECHANISM_TWAP) {
    band.twapDurationSeconds = b.twapDurationSeconds
  }
  return band
}

export function buildScope(form: HedgeFormModel): HedgeScopeResolved {
  if (form.scopeMode === 'underlying') {
    return { portfolioUnderlying: { asset: form.underlyingAsset.trim() } }
  }
  if (form.scopeMode === 'positionGroup') {
    return {
      positionGroup: {
        positionSymbols: form.positionSymbols,
        label: form.positionGroupLabel.trim() || undefined,
      },
    }
  }
  // Whole portfolio == empty scope oneof.
  return {}
}

// Only an explicit, single-underlying scope can send an instrument at all — for a
// multi-underlying scope it's left empty (proto3 omits an empty string on the wire) so the
// backend expands into one hedge per underlying on its own.
//
// When the scope *does* allow one but the user never opened the override (hedgeInstrument
// form field still blank), don't leave it for the backend to derive from the bare asset
// string either — for a base currency containing an underscore (e.g. HYPE_USDC), the
// backend's own asset->instrument derivation has been observed doing the same naive
// "strip everything after the first underscore" as this file's own underlyingOf used to,
// producing HYPE-PERPETUAL (not found) instead of HYPE_USDC-PERPETUAL. Resolve the same
// default perpetual the UI already previews (resolveDefaultHedgeInstrument, hedge-instrument-
// field.tsx) and send its real symbol explicitly so the backend never has to guess.
export function resolveHedgeInstrument(
  form: HedgeFormModel,
  positions: Position[],
  instrumentMap: Map<string, Instrument>,
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>
): string {
  const scope = hedgeInstrumentScope(form, positions, instrumentMap, multiExchangeInstrumentMap)
  const explicitInstrument = form.hedgeInstrument.trim()
  return scope.allowsExplicit
    ? explicitInstrument ||
        (scope.underlying
          ? (resolveDefaultHedgeInstrument(Array.from(instrumentMap.values()), scope.underlying)
              ?.symbol ?? '')
          : '')
    : ''
}

export function buildHedgeParams(
  form: HedgeFormModel,
  accountId: string,
  positions: Position[],
  instrumentMap: Map<string, Instrument>,
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>
): HedgeAlgoParams {
  const deltaBands: HedgeDeltaBandsResolved = {
    acceptable: {
      from: form.acceptable.from,
      to: form.acceptable.to,
      target: form.acceptable.target,
    },
    denomination: form.denomination,
  }
  for (const key of PRIORITY_RUNG_KEYS) {
    const b = form[key]
    if (b.enabled) deltaBands[key] = priorityBand(b)
  }

  const hedgeInstrument = resolveHedgeInstrument(
    form,
    positions,
    instrumentMap,
    multiExchangeInstrumentMap
  )

  const params: HedgeAlgoParams = {
    accountId,
    exchange: Exchange.EXCHANGE_UNSPECIFIED,
    tradeVenue: TradeVenue.TRADE_VENUE_UNSPECIFIED,
    scope: buildScope(form),
    deltaBands,
    hedgeInstrument,
    positiveGammaTreatment: form.modeKind === 'interval' ? false : form.positiveGammaTreatment,
    replaceOverlapping: form.replaceOverlapping,
  }

  if (form.modeKind === 'interval') {
    params.interval = { intervalSeconds: form.executionSeconds }
  }

  return params
}
